<script setup>
import { onMounted, onUnmounted, computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePipelinePermissions } from 'dashboard/composables/pipelines/usePipelinePermissions';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelSidebar from './ChannelSidebar.vue';
import ChannelView from './ChannelView.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { can, isAdmin } = usePipelinePermissions();

const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);

function onResize() {
  isMobile.value = window.innerWidth < 768;
}

const activeChannelId = computed(() => {
  return Number(route.params.channelId) || null;
});

const activeChannel = computed(() => {
  if (!activeChannelId.value) return null;
  return store.getters['internalChat/getChannelById'](activeChannelId.value);
});

const isDraftsRoute = computed(() => {
  return route.name === 'internal_chat_drafts';
});

// On mobile, content area is shown when a channel is selected or on the drafts route
const showContentOnMobile = computed(() => !!activeChannelId.value || isDraftsRoute.value);

function handleBackToSidebar() {
  router.push({
    name: 'internal_chat_home',
    params: { accountId: route.params.accountId },
  });
}

async function fetchChannels() {
  try {
    await store.dispatch('internalChat/get');
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.FETCH_CHANNELS'));
  }
}

onMounted(async () => {
  window.addEventListener('resize', onResize);
  if (!isAdmin.value && !can('internal_chat.view')) {
    router.replace({ name: 'home' });
    return;
  }
  await fetchChannels();
  // If navigated directly to a channel not in the store (e.g. archived), fetch it
  if (activeChannelId.value && !activeChannel.value) {
    store.dispatch('internalChat/show', activeChannelId.value).catch(() => {});
  }
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <div class="flex h-full w-full">
    <!-- Sidebar: always visible on desktop; on mobile only when no channel/drafts selected -->
    <ChannelSidebar
      v-if="!isMobile || !showContentOnMobile"
      :is-mobile="isMobile"
    />
    <!-- Content area: always visible on desktop; on mobile only when channel/drafts selected -->
    <div v-if="!isMobile || showContentOnMobile" class="flex-1 min-w-0">
      <ChannelView
        v-if="activeChannelId && activeChannel"
        :key="activeChannelId"
        :channel-id="activeChannelId"
        :is-mobile="isMobile"
        @back-to-sidebar="handleBackToSidebar"
      />
      <router-view v-else-if="isDraftsRoute" />
      <div
        v-else
        class="flex h-full flex-col items-center justify-center gap-2 bg-n-solid-1"
      >
        <Icon icon="i-lucide-messages-square" class="size-10 text-n-slate-8" />
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INTERNAL_CHAT.EMPTY_STATE.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t('INTERNAL_CHAT.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
